import Phaser from 'phaser';
import { Upgrade } from '../types/Upgrades';

export class UpgradeUI {
  private scene: Phaser.Scene;
  private uiElements: Phaser.GameObjects.GameObject[] = [];
  private background!: Phaser.GameObjects.Rectangle;
  private onUpgradeSelected?: (upgrade: Upgrade) => void;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  public show(upgrades: Upgrade[], callback: (upgrade: Upgrade) => void): void {
    this.onUpgradeSelected = callback;

    // Create semi-transparent background
    this.background = this.scene.add.rectangle(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY,
      this.scene.cameras.main.width,
      this.scene.cameras.main.height,
      0x000000,
      0.8
    );
    this.background.setScrollFactor(0);
    this.background.setDepth(1000);
    this.uiElements.push(this.background);

    // Title
    const title = this.scene.add.text(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY - 150,
      'LEVEL UP!',
      {
        fontSize: '48px',
        color: '#FFD700',
        fontStyle: 'bold'
      }
    );
    title.setOrigin(0.5);
    title.setScrollFactor(0);
    title.setDepth(1001);
    this.uiElements.push(title);

    const subtitle = this.scene.add.text(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY - 100,
      'Choose an upgrade:',
      {
        fontSize: '24px',
        color: '#FFFFFF'
      }
    );
    subtitle.setOrigin(0.5);
    subtitle.setScrollFactor(0);
    subtitle.setDepth(1001);
    this.uiElements.push(subtitle);

    // Create upgrade option cards
    const startX = this.scene.cameras.main.centerX - 350;
    const y = this.scene.cameras.main.centerY;
    const spacing = 250;

    upgrades.forEach((upgrade, index) => {
      const cardX = startX + (index * spacing);
      this.createUpgradeCard(upgrade, cardX, y);
    });

    // Pause the game
    this.scene.physics.pause();
  }

  private createUpgradeCard(upgrade: Upgrade, x: number, y: number): void {
    // Card background
    const card = this.scene.add.rectangle(x, y, 220, 280, 0x2a2a2a, 1);
    card.setStrokeStyle(3, 0x4a4a4a);
    card.setScrollFactor(0);
    card.setDepth(1001);
    this.uiElements.push(card);

    // Icon
    const icon = this.scene.add.text(x, y - 80, upgrade.icon, {
      fontSize: '48px'
    });
    icon.setOrigin(0.5);
    icon.setScrollFactor(0);
    icon.setDepth(1002);
    this.uiElements.push(icon);

    // Name
    const name = this.scene.add.text(x, y - 20, upgrade.name, {
      fontSize: '18px',
      color: '#FFD700',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: 200 }
    });
    name.setOrigin(0.5);
    name.setScrollFactor(0);
    name.setDepth(1002);
    this.uiElements.push(name);

    // Description
    const description = this.scene.add.text(x, y + 30, upgrade.description, {
      fontSize: '14px',
      color: '#CCCCCC',
      align: 'center',
      wordWrap: { width: 200 }
    });
    description.setOrigin(0.5);
    description.setScrollFactor(0);
    description.setDepth(1002);
    this.uiElements.push(description);

    // Make card interactive
    card.setInteractive({ useHandCursor: true });

    card.on('pointerover', () => {
      card.setStrokeStyle(3, 0xFFD700);
      card.setFillStyle(0x3a3a3a);
    });

    card.on('pointerout', () => {
      card.setStrokeStyle(3, 0x4a4a4a);
      card.setFillStyle(0x2a2a2a);
    });

    card.on('pointerdown', () => {
      this.selectUpgrade(upgrade);
    });
  }

  private selectUpgrade(upgrade: Upgrade): void {
    if (this.onUpgradeSelected) {
      this.onUpgradeSelected(upgrade);
    }

    this.hide();
  }

  private hide(): void {
    // Remove all UI elements
    this.uiElements.forEach(element => {
      element.destroy();
    });
    this.uiElements = [];

    // Resume the game
    this.scene.physics.resume();
  }
}
